<section class="late-fees-section" [formGroup]="form">

    <button type="button"
            class="late-fees-section__header"
            (click)="toggleExpanded()"
            [attr.aria-expanded]="isExpanded()">
        <fa-duotone-icon class="late-fees-section__header-chevron"
                          [icon]="isExpanded() ? 'chevron-up' : 'chevron-down'"
                          secondaryOpacity="0.9" />
        <span class="late-fees-section__header-left">
            <span class="late-fees-section__title-row">
                <span class="late-fees-section__title">Late Fees</span>
                <span class="late-fees-section__title-sub">(optional)</span>
            </span>
            @if ((isEnabled() && tiersArray.length > 0) || isLocked()) {
                <span class="late-fees-section__header-badges">
                    @if (isEnabled() && tiersArray.length > 0) {
                        <span class="late-fees-section__badge">
                            {{ tiersArray.length }}
                            {{ tiersArray.length === 1 ? 'tier' : 'tiers' }}
                        </span>
                    }
                    @if (isLocked()) {
                        <span class="late-fees-section__badge late-fees-section__badge--locked">
                            <fa-duotone-icon icon="lock" secondaryOpacity="0.9" class="me-1" />Locked
                        </span>
                    }
                </span>
            }
        </span>
        <span class="late-fees-section__header-right">
            @if (isExpanded()) {
                Collapse
            } @else {
                Configure
            }
        </span>
    </button>

    @if (isExpanded()) {
        <div class="late-fees-section__body">

            @if (isLocked()) {
                @if (isEnabled()) {
                    <div class="alert alert-warning d-flex align-items-center mb-3" role="alert">
                        <fa-duotone-icon icon="lock" class="me-2" secondaryOpacity="0.3" />
                        <small class="mb-0">{{ lockedBannerMessage }}</small>
                    </div>
                } @else {
                    <div class="alert alert-info d-flex align-items-center mb-0" role="alert">
                        <fa-duotone-icon icon="circle-info" class="me-2" secondaryOpacity="0.3" />
                        <small class="mb-0">{{ lockedNoConfigBannerMessage }}</small>
                    </div>
                }
            }

            @if (!isLocked()) {
                <div class="d-flex align-items-center mb-3">
                    <div class="form-check form-switch fs-5">
                        <input
                            class="form-check-input"
                            type="checkbox"
                            id="enableLateFees"
                            [checked]="enableLateFeesControl.value"
                            (change)="onEnableToggleChanged($event)">
                        <label class="form-check-label fs-6" for="enableLateFees">
                            Enable late fees
                        </label>
                    </div>
                </div>
            }

            @if (isEnabled()) {

                @if (!isLocked()) {

                <div class="row g-3 mb-3">
                    <div class="col-sm-6">
                        <ms-input-label [isRequired]="true" [labelText]="'Late Fee Revenue Account'"
                            [tooltip]="InvoiceScheduleHelpText.LateFeeRevenueAccount" />
                        <ms-chart-of-account-typeahead
                            #lateFeeRevenueAccountTypeahead
                            id="lateFeeRevenueAccount"
                            formControlName="lateFeeRevenueAccount"
                            [organizationId]="organizationId()"
                            [categories]="[ChartOfAccountCategory.Revenue]"
                            [invalid]="lateFeeRevenueAccountControl.invalid && lateFeeRevenueAccountControl.touched ? true : false" />
                        @if (lateFeeRevenueAccountTypeahead.results().length === 0 && !lateFeeRevenueAccountTypeahead.isSearching()) {
                            <p class="text-warning small mb-0 mt-1">
                                <fa-duotone-icon icon="triangle-exclamation" class="me-1" secondaryOpacity="0.3" />
                                A revenue chart of account must be configured for this organization before late fees can be saved.
                            </p>
                        } @else if (lateFeeRevenueAccountControl.invalid && lateFeeRevenueAccountControl.touched) {
                            <p class="text-danger text-start pt-1 mb-0">{{ FormValidatorMessages.LateFeesRevenueAccountRequired }}</p>
                        }
                    </div>
                </div>

                <div class="late-fee-tiers">
                    @for (tier of tierGroups(); track tierIds[$index]; let i = $index) {
                        <div class="late-fee-tier" [formGroup]="tier">
                            <div class="late-fee-tier__header">
                                <span class="late-fee-tier__title">Tier {{ i + 1 }}</span>
                                <button type="button"
                                        class="btn btn-link p-0 text-danger late-fee-tier__remove"
                                        (click)="removeTier(i)"
                                        [disabled]="isLocked()"
                                        title="Remove tier"
                                        aria-label="Remove tier">
                                    <fa-duotone-icon icon="trash" secondaryOpacity="0.9" />
                                </button>
                            </div>

                            <div class="row g-3">
                                <div class="col-sm-6">
                                    <ms-input-label [isRequired]="true" [labelText]="'Fees Amount'" />
                                    <ms-amount-input formControlName="amount" />
                                    @if (getTierGroup(i).get('amount')?.touched && getTierGroup(i).get('amount')?.invalid) {
                                        @if (getTierGroup(i).get('amount')?.errors?.['required']) {
                                            <p class="text-danger small mb-0 mt-1">{{ FormValidatorMessages.LateFeeTierAmountRequired }}</p>
                                        } @else {
                                            <p class="text-danger small mb-0 mt-1">{{ FormValidatorMessages.LateFeeTierAmountMin }}</p>
                                        }
                                    }
                                </div>

                                <div class="col-sm-6">
                                    <ms-input-label [isRequired]="true" [labelText]="'Grace Period (days)'" />
                                    <input type="number"
                                           class="form-control"
                                           formControlName="gracePeriodDays"
                                           min="0"
                                           max="999"
                                           inputmode="numeric"
                                           [class.is-invalid]="getTierGroup(i).get('gracePeriodDays')?.invalid && getTierGroup(i).get('gracePeriodDays')?.touched"
                                           [class.is-valid]="getTierGroup(i).get('gracePeriodDays')?.valid && getTierGroup(i).get('gracePeriodDays')?.touched && getTierGroup(i).get('gracePeriodDays')?.value !== null && getTierGroup(i).get('gracePeriodDays')?.value !== ''" />
                                    <div class="form-text">Number of days after the invoice due date before the late fee applies.</div>
                                    @if (getTierGroup(i).get('gracePeriodDays')?.touched && getTierGroup(i).get('gracePeriodDays')?.invalid) {
                                        <p class="text-danger small mb-0 mt-1">{{ FormValidatorMessages.LateFeeTierGracePeriodRequired }}</p>
                                    }
                                </div>
                            </div>

                            <div class="row g-3 mt-1">
                                <div class="col-sm-6">
                                    <ms-input-label [isRequired]="true" [labelText]="'Occurrences'" />
                                    <select formControlName="occurrenceType"
                                            class="form-select"
                                            [class.is-invalid]="getTierGroup(i).get('occurrenceType')?.invalid && getTierGroup(i).get('occurrenceType')?.touched"
                                            [class.is-valid]="getTierGroup(i).get('occurrenceType')?.valid && getTierGroup(i).get('occurrenceType')?.touched">
                                        <option value="Once">Once — applies only once</option>
                                        <option value="Multiple">Multiple — can recur a set number of times</option>
                                        <option value="UntilPaid">Until paid — recurs until invoice is paid</option>
                                    </select>
                                </div>

                                @if (getTierOccurrenceType(i) === 'Multiple') {
                                    <div class="col-sm-3">
                                        <ms-input-label [isRequired]="true" [labelText]="'Number of times'" />
                                        <input type="number"
                                               class="form-control"
                                               formControlName="multipleCount"
                                               min="2"
                                               max="999"
                                               inputmode="numeric"
                                               [class.is-invalid]="getTierGroup(i).get('multipleCount')?.invalid && getTierGroup(i).get('multipleCount')?.touched"
                                               [class.is-valid]="getTierGroup(i).get('multipleCount')?.valid && getTierGroup(i).get('multipleCount')?.touched && getTierGroup(i).get('multipleCount')?.value !== null && getTierGroup(i).get('multipleCount')?.value !== ''" />
                                        <div class="form-text">Number of times the late fee can recur.</div>
                                        @if (getTierGroup(i).get('multipleCount')?.touched && getTierGroup(i).get('multipleCount')?.invalid) {
                                            @if (getTierGroup(i).get('multipleCount')?.errors?.['required']) {
                                                <p class="text-danger small mb-0 mt-1">{{ FormValidatorMessages.LateFeeTierMultipleCountRequired }}</p>
                                            } @else {
                                                <p class="text-danger small mb-0 mt-1">{{ FormValidatorMessages.LateFeeTierMultipleCountMin }}</p>
                                            }
                                        }
                                    </div>
                                }

                                @if (getTierOccurrenceType(i) === 'Multiple' || getTierOccurrenceType(i) === 'UntilPaid') {
                                    <div class="col-sm-3">
                                        <ms-input-label [isRequired]="true" [labelText]="'Frequency (days)'" />
                                        <input type="number"
                                               class="form-control"
                                               formControlName="frequencyDays"
                                               min="1"
                                               max="999"
                                               inputmode="numeric"
                                               [class.is-invalid]="getTierGroup(i).get('frequencyDays')?.invalid && getTierGroup(i).get('frequencyDays')?.touched"
                                               [class.is-valid]="getTierGroup(i).get('frequencyDays')?.valid && getTierGroup(i).get('frequencyDays')?.touched && getTierGroup(i).get('frequencyDays')?.value !== null && getTierGroup(i).get('frequencyDays')?.value !== ''" />
                                        <div class="form-text">Interval in days between recurrences.</div>
                                        @if (getTierGroup(i).get('frequencyDays')?.touched && getTierGroup(i).get('frequencyDays')?.invalid) {
                                            <p class="text-danger small mb-0 mt-1">{{ FormValidatorMessages.LateFeeTierFrequencyRequired }}</p>
                                        }
                                    </div>
                                }
                            </div>
                        </div>
                    }
                </div>

                <div class="late-fees-section__add">
                    <button type="button"
                            class="btn btn-ms-secondary"
                            (click)="addTier()"
                            [disabled]="hasMaxTiers()">
                        <fa-duotone-icon icon="plus" secondaryOpacity="0.9" class="me-1" />
                        Add Tier
                    </button>
                    @if (hasMaxTiers()) {
                        <span class="form-text ms-2">
                            Maximum of {{ maxTiers }} tiers reached. Remove a tier to add another.
                        </span>
                    }
                </div>

                }

                <div class="late-fees-section__timeline">
                    <ms-late-fee-timeline
                        [dueDate]="dueDate()"
                        [tiers]="tierModels()" />
                </div>
            }
        </div>
    }
</section>
